import {
  Button,
  Card,
  Col,
  Divider,
  Dropdown,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Row,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from "antd";
import { ApiService } from "api/api.service";
import { AddressInfo, LeadStatus, MakingPayments } from "dtos/lead.dto";
import { useState } from "react";
import {
  TbCircleFilled,
  TbClipboardCheck,
  TbCopy,
  TbDetails,
  TbEdit,
  TbLoader2,
  TbMail,
  TbPencilMinus,
  TbPhone,
  TbX,
} from "react-icons/tb";
import { LEAD_STATUS_COLOURS } from "utils/constants";
import { formatDateTime, formatMoney, phoneFormatter } from "utils/functions";
import { PopulatedLeadDto } from "../Dashboard.columns";
import { type BusinessInfo, type ClientDetails, SelectedBank, StrategyType } from "dtos/application.dto";

type Props = {
  selectedLead: PopulatedLeadDto;
  onSiderClose: () => void;
};

export default function LeadSiderContent({ selectedLead: selectedLeadFromAll, onSiderClose }: Props) {
  const lead = ApiService.lead.useGetByIdOrRayId(selectedLeadFromAll.ray_id, selectedLeadFromAll);
  const selectedLead = lead.data ?? selectedLeadFromAll;

  const updateRemarks = ApiService.lead.useUpdateStatusOrRemarks();

  function handleChangeStatus(status: LeadStatus) {
    if (selectedLead) {
      updateRemarks.mutate(
        {
          _id: selectedLead._id,
          status: status,
        },
        {
          onSuccess: () => {
            message.success(`Status change to ${status}`);
          },
        },
      );
    }
  }

  return (
    <>
      <Flex className="pt-3 px-4" justify="space-between">
        <Tag className="h-fit" color={LEAD_STATUS_COLOURS[selectedLead.status]}>
          {selectedLead.status?.toUpperCase()}
        </Tag>
        <Flex gap={8} align="center">
          {lead.isFetching ? (
            <Typography.Link>
              <TbLoader2 className="animate-spin" />
            </Typography.Link>
          ) : null}
          <Button type="text" onClick={onSiderClose} icon={<TbX />} />
        </Flex>
      </Flex>
      <div className="p-4">
        <Typography.Title level={3}>{selectedLead.name}</Typography.Title>
        <Flex gap={4} vertical className="mt-2">
          {selectedLead.email ? (
            <Typography.Text
              type="secondary"
              copyable={{
                text: selectedLead.email,
                icon: [
                  <TbCopy key="copy-icon" className="flex-shrink-0" />,
                  <TbClipboardCheck key="copied-icon" className="flex-shrink-0" />,
                ],
                tooltips: ["Copy", "Copied"],
              }}
              className="flex items-center"
            >
              <TbMail className="mr-2 flex-shrink-0" />
              {selectedLead.email}
            </Typography.Text>
          ) : null}
          {selectedLead.phone ? (
            <Typography.Text
              type="secondary"
              copyable={{
                text: selectedLead.phone,
                icon: [<TbCopy key="copy-icon" />, <TbClipboardCheck key="copied-icon" />],
                tooltips: ["Copy", "Copied"],
              }}
              className="flex items-center"
            >
              <TbPhone className="mr-2 flex-shrink-0" />
              {selectedLead.phone}
            </Typography.Text>
          ) : null}
        </Flex>
        <Flex className="mt-4">
          <Dropdown.Button
            trigger={["click"]}
            type="primary"
            loading={updateRemarks.isPending}
            menu={{
              selectable: true,
              selectedKeys: [selectedLead.status],
              items: Object.entries(LeadStatus).map(([key, val]) => {
                return {
                  label: <Tag color={LEAD_STATUS_COLOURS[val]}>{key.replace(/_/, " ")}</Tag>,
                  key: val,
                };
              }),
              onClick: (i) => handleChangeStatus(i.key as LeadStatus),
            }}
          >
            Change Status
          </Dropdown.Button>
        </Flex>
        <Divider className="mb-0" />
        <Tabs
          defaultActiveKey="debt_details"
          items={[
            {
              key: "debt_details",
              label: "Debt Details",
              children: <DebtDetails selectedLead={selectedLead} />,
            },
            {
              key: "activity_log",
              label: "Activity Log",
              children: <ActivityLog logs={selectedLead?.logs} />,
            },
          ]}
        />
      </div>
    </>
  );
}

export function DebtDetails({ selectedLead }: { selectedLead: PopulatedLeadDto }) {
  const [addressOfLead, setAddressOfLead] = useState<PopulatedLeadDto>();
  const [remarksOfLead, setRemarksOfLead] = useState<PopulatedLeadDto>();

  return (
    <>
      <Typography.Title level={5}>Debt Details</Typography.Title>
      <Row>
        <Col span={12}>
          <Typography.Text type="secondary">Debt Amount</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>{formatMoney(selectedLead.debt_amount)}</Typography.Text>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <Typography.Text type="secondary">Payment Amount</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>
            {selectedLead.questionaire?.payment_amount ? formatMoney(selectedLead.questionaire?.payment_amount) : "-"}
          </Typography.Text>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <Typography.Text type="secondary">Currently Paying</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>
            {selectedLead.questionaire?.is_making_payments ? (
              <Tag color="default">{selectedLead.questionaire?.is_making_payments}</Tag>
            ) : (
              "-"
            )}
          </Typography.Text>
        </Col>
      </Row>
      {selectedLead.questionaire?.is_making_payments === MakingPayments.YES ? (
        <Row>
          <Col span={12}>
            <Typography.Text type="secondary">Payment Frequency</Typography.Text>
          </Col>
          <Col>
            {selectedLead.questionaire?.payment_frequency ? (
              <Tag color="default">{selectedLead.questionaire?.payment_frequency}</Tag>
            ) : (
              "-"
            )}
          </Col>
        </Row>
      ) : null}
      {selectedLead.questionaire?.is_making_payments &&
      [MakingPayments.NO, MakingPayments.SOMETIMES].includes(selectedLead.questionaire?.is_making_payments) ? (
        <Row>
          <Col span={12}>
            <Typography.Text type="secondary">Pending Lawsuit</Typography.Text>
          </Col>
          <Col>
            <Typography.Text>
              {selectedLead.questionaire?.has_pending_lawsuit ? (
                <Tag color="default">{selectedLead.questionaire?.has_pending_lawsuit}</Tag>
              ) : (
                "-"
              )}
            </Typography.Text>
          </Col>
        </Row>
      ) : null}

      {/* <Divider className="mb-4" />
        <Typography.Title level={5}>Message</Typography.Title>
        <Typography.Paragraph>{selectedLead.message}</Typography.Paragraph> */}

      <Divider className="mb-4" />
      <Flex justify="space-between" className="mb-2">
        <Typography.Title level={5}>Remarks</Typography.Title>
        <Button type="dashed" icon={<TbPencilMinus />} onClick={() => setRemarksOfLead(selectedLead)} />
      </Flex>
      <Typography.Paragraph>{selectedLead.remarks?.length ? selectedLead.remarks : "-"}</Typography.Paragraph>

      <Divider className="mb-4" />
      <Flex justify="space-between" className="mb-2">
        <Typography.Title level={5}>Address</Typography.Title>
        <Button type="dashed" icon={<TbPencilMinus />} onClick={() => setAddressOfLead(selectedLead)} />
      </Flex>
      {selectedLead.address?.country ? (
        <Typography.Paragraph>
          <span>
            {selectedLead.address?.house_no}, {selectedLead.address?.street}
          </span>
          <br />
          <span>{selectedLead.address?.second_line}</span>
          <br />
          <span>
            {selectedLead.address?.locality}-{selectedLead.address?.zipcode}
          </span>
          <br />
          <span>
            {selectedLead.address?.state}, {selectedLead.address?.country}
          </span>
        </Typography.Paragraph>
      ) : null}

      {/* ########### Modals ########### */}
      {addressOfLead ? (
        <UpdateAddressModal
          addressOfLead={addressOfLead}
          onCancel={() => {
            setAddressOfLead(undefined);
          }}
        />
      ) : null}
      {remarksOfLead ? (
        <UpdateRemarksModal
          remarksOfLead={remarksOfLead}
          onCancel={() => {
            setRemarksOfLead(undefined);
          }}
        />
      ) : null}
    </>
  );
}

export function ClientDetails({ clientDetails }: { clientDetails: ClientDetails }) {
  return (
    <>
      <Typography.Title level={5}>Client Details</Typography.Title>
      <Row>
        <Col span={8}>
          <Typography.Text type="secondary">Name</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>{clientDetails?.name ? clientDetails?.name : "-"}</Typography.Text>
        </Col>
      </Row>
      <Row>
        <Col span={8}>
          <Typography.Text type="secondary">Phone No</Typography.Text>
        </Col>
        <Col>
          <Typography.Text
            copyable={{
              text: clientDetails?.phone ? phoneFormatter(clientDetails?.phone) : "-",
              icon: [
                <TbCopy key="copy-icon" className="flex-shrink-0" />,
                <TbClipboardCheck key="copied-icon" className="flex-shrink-0" />,
              ],
              tooltips: ["Copy", "Copied"],
            }}
          >
            {clientDetails?.phone ? phoneFormatter(clientDetails?.phone) : "-"}
          </Typography.Text>
        </Col>
      </Row>
      <Row>
        <Col span={8}>
          <Typography.Text type="secondary">SSN No</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>{clientDetails?.ssn ? clientDetails?.ssn : "-"}</Typography.Text>
        </Col>
      </Row>
    </>
  );
}

const LabelCol = ({ children }: { children: React.ReactNode }) => (
  <Col flex="140px">
    <Typography.Text type="secondary">{children}</Typography.Text>
  </Col>
);

const ValueCol = ({ children }: { children: React.ReactNode }) => (
  <Col flex="auto">
    <div className="break-words">{children}</div>
  </Col>
);

export function BusinessInfo({
  businessInfo,
  clientDetails,
  setBusinessInfo,
  openModal,
  setClientDetails,
  setBankDetails,
}: {
  businessInfo: BusinessInfo;
  clientDetails: ClientDetails;
  setBusinessInfo: (info: BusinessInfo) => void;
  setClientDetails: (info: ClientDetails) => void;
  setBankDetails: () => void;
  openModal: () => void;
}) {
  return (
    <Card
      title={
        <div className="flex justify-between items-center">
          <Typography.Title level={5} style={{ margin: 0 }}>
            Business Info
          </Typography.Title>
          <Button
            onClick={() => {
              openModal();
              setBusinessInfo(businessInfo);
              setClientDetails(clientDetails);
              setBankDetails();
            }}
            type="text"
            size="small"
            icon={<TbEdit />}
          ></Button>
        </div>
      }
      size="small"
      className="rounded-lg"
    >
      <Row gutter={[8, 6]}>
        <LabelCol>Client Name</LabelCol>
        <ValueCol>
          <Typography.Text className="!max-w-[200px]" ellipsis={{ tooltip: businessInfo?.company_name }}>
            {clientDetails?.name || "—"}
          </Typography.Text>
        </ValueCol>
      </Row>

      <Row gutter={[8, 6]}>
        <LabelCol>Company Name</LabelCol>
        <ValueCol>
          <Typography.Text className="!max-w-[200px]" ellipsis={{ tooltip: businessInfo?.company_name }}>
            {businessInfo?.company_name || "—"}
          </Typography.Text>
        </ValueCol>
      </Row>

      <Row gutter={[8, 6]}>
        <LabelCol>SSN</LabelCol>
        <ValueCol>
          <Typography.Text
            copyable={{
              text: clientDetails?.ssn || "N/A",
              tooltips: ["Copy", "Copied"],
            }}
          >
            {clientDetails?.ssn || "N/A"}
          </Typography.Text>
        </ValueCol>
      </Row>

      <Row gutter={[8, 6]}>
        <LabelCol>Phone No</LabelCol>
        <ValueCol>
          <Typography.Text
            copyable={{
              text: businessInfo?.phone ? phoneFormatter("1" + businessInfo.phone.replace(/-/g, "")) : "N/A",
              tooltips: ["Copy", "Copied"],
            }}
          >
            {businessInfo?.phone ? phoneFormatter("1" + businessInfo.phone.replace(/-/g, "")) : "N/A"}
          </Typography.Text>
        </ValueCol>
      </Row>

      <Row gutter={[8, 6]}>
        <LabelCol>EIN Number</LabelCol>
        <ValueCol>
          <Typography.Text>{businessInfo?.ein_number || "N/A"}</Typography.Text>
        </ValueCol>
      </Row>

      <Row gutter={[8, 6]}>
        <LabelCol>Address</LabelCol>
        <ValueCol>
          <Typography.Text
            ellipsis={{
              tooltip: businessInfo?.street_address
                ? [businessInfo.street_address, businessInfo.city, businessInfo.state, businessInfo.zip_code].join(", ")
                : "N/A",
            }}
            className="!max-w-[200px]"
          >
            {businessInfo?.street_address
              ? [businessInfo.street_address, businessInfo.city, businessInfo.state, businessInfo.zip_code].join(", ")
              : "N/A"}
          </Typography.Text>
        </ValueCol>
      </Row>
    </Card>
  );
}

export function ApplicationDetails({
  selectedLead,
}: {
  selectedLead: {
    total_loans?: number;
    total_banks?: number;
    total_lawsuits?: number;
    enrolled_debt?: number;
    last_step?: string | "";
    onboarding_completed?: boolean;
  };
}) {
  return (
    <>
      {/* <Typography.Title level={5}>Application Details</Typography.Title> */}

      <Row>
        <Col span={14}>
          <Typography.Text type="secondary">Loans</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>{selectedLead.total_loans ? selectedLead.total_loans : "-"}</Typography.Text>
        </Col>
      </Row>
      <Row>
        <Col span={14}>
          <Typography.Text type="secondary">Banks</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>{selectedLead.total_banks ? selectedLead.total_banks : "-"}</Typography.Text>
        </Col>
      </Row>
      <Row>
        <Col span={14}>
          <Typography.Text type="secondary">Lawsuits</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>{selectedLead.total_lawsuits ? selectedLead.total_lawsuits : "-"}</Typography.Text>
        </Col>
      </Row>
      {/* <Row>
        <Col span={14}>
          <Typography.Text type="secondary">Enrolled Debt</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>
            {selectedLead.enrolled_debt ? formatMoney(selectedLead.enrolled_debt) : "-"}
          </Typography.Text>
        </Col>
      </Row> */}
      {/* <Row>
        <Col span={14}>
          <Typography.Text type="secondary">Last Step</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>{selectedLead.last_step ? selectedLead.last_step : "-"}</Typography.Text>
        </Col>
      </Row> */}
    </>
  );
}

export function StrategyPlanDetails({
  planDetails,
}: Readonly<{
  planDetails: Partial<{
    totalDebt: number;
    initial_payment: number;
    weekly_payments: number;
    tenure: number;
    lawsuit_initial_payment: number;
    lawsuit_monthly_payment: number;
    mode: StrategyType;
  }>;
}>) {
  return (
    <>
      <Row className="mb-2">
        <Col span={24}>
          <Typography.Text type="secondary" className="text-xl">
            Total Debt
          </Typography.Text>
        </Col>
        <Col>
          <Typography.Text className="font-bold text-2xl">
            {planDetails.totalDebt ? formatMoney(planDetails.totalDebt) : "-"}
          </Typography.Text>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col span={24}>
          <Typography.Text type="secondary">Initial Payments</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>
            {planDetails?.initial_payment ? formatMoney(planDetails?.initial_payment) : "-"}
          </Typography.Text>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col span={24}>
          <Typography.Text type="secondary">Weekly Payments</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>
            {planDetails?.weekly_payments ? formatMoney(planDetails?.weekly_payments) : "-"}
          </Typography.Text>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col span={24}>
          <Typography.Text type="secondary">Tenure</Typography.Text>
        </Col>
        <Col>
          {planDetails.mode === StrategyType.PERCENTAGE ? "~" : ""}{" "}
          <Typography.Text>{planDetails?.tenure ? `${planDetails?.tenure} weeks` : "-"}</Typography.Text>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col span={24}>
          <Typography.Text type="secondary">Lawsuit Initial Payments</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>
            {planDetails?.lawsuit_initial_payment ? formatMoney(planDetails?.lawsuit_initial_payment) : "-"}
          </Typography.Text>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col span={24}>
          <Typography.Text type="secondary">Lawsuit Monthly Payments</Typography.Text>
        </Col>
        <Col>
          <Typography.Text>
            {planDetails?.lawsuit_monthly_payment ? formatMoney(planDetails?.lawsuit_monthly_payment) : "-"}
          </Typography.Text>
        </Col>
      </Row>
    </>
  );
}

export function ActivityLog({ logs }: { logs: PopulatedLeadDto["logs"] }) {
  return (
    <Timeline
      className="ml-3"
      items={logs?.map((l) => {
        return {
          children: (
            <>
              <Typography.Text type="secondary">
                <small>{formatDateTime(l.timestamp)}</small>
              </Typography.Text>
              <br />
              <Typography.Text>{l.message}</Typography.Text>
            </>
          ),
          dot: (
            <Tag
              className="size-4 p-[3px] flex items-center justify-center rounded-full"
              icon={<TbCircleFilled />}
              color={LEAD_STATUS_COLOURS[l.status]}
            />
          ),
        };
      })}
    />
  );
}

function UpdateRemarksModal({
  remarksOfLead,
  onCancel,
}: {
  remarksOfLead: PopulatedLeadDto | undefined;
  onCancel: () => void;
}) {
  const updateRemarks = ApiService.lead.useUpdateStatusOrRemarks();
  const [form] = Form.useForm();

  const handleClose = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      open={!!remarksOfLead}
      onCancel={handleClose}
      title="Update remarks"
      okText="Update"
      okButtonProps={{
        loading: updateRemarks.isPending,
        onClick: () => {
          form.submit();
        },
      }}
    >
      <Form
        layout="vertical"
        initialValues={{ remarks: remarksOfLead?.remarks }}
        form={form}
        onFinish={(values) => {
          if (remarksOfLead) {
            updateRemarks.mutate(
              {
                _id: remarksOfLead._id,
                remarks: values?.remarks,
              },
              {
                onSuccess: handleClose,
              },
            );
          }
        }}
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item name={"remarks"} label="Remarks">
              <Input.TextArea autoSize={{ minRows: 3, maxRows: 11 }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

function UpdateAddressModal({
  addressOfLead,
  onCancel,
}: {
  addressOfLead: PopulatedLeadDto | undefined;
  onCancel: () => void;
}) {
  const updateAddress = ApiService.lead.useUpdateAddress();
  const [form] = Form.useForm();

  const handleClose = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      open={!!addressOfLead}
      onCancel={handleClose}
      title="Update lead address"
      okText="Update"
      okButtonProps={{
        loading: updateAddress.isPending,
        onClick: () => {
          form.submit();
        },
      }}
    >
      <Form
        layout="vertical"
        initialValues={addressOfLead?.address}
        form={form}
        onFinish={(values) => {
          if (addressOfLead) {
            updateAddress.mutate(
              {
                _id: addressOfLead._id,
                address: values,
              },
              {
                onSuccess: handleClose,
              },
            );
          }
        }}
      >
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Form.Item<AddressInfo> name={"house_no"} label="House No.">
              <Input />
            </Form.Item>
          </Col>
          <Col span={18}>
            <Form.Item<AddressInfo> name={"street"} label="Street">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item<AddressInfo> name={"second_line"} label="Second Line">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={18}>
            <Form.Item<AddressInfo> name={"locality"} label="City">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item<AddressInfo> name={"zipcode"} label="Zip Code">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item<AddressInfo> name={"state"} label="State">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item<AddressInfo> name={"country"} label="Country">
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
